import { useEffect, useRef } from 'react';
import { VideoOff } from 'lucide-react';
import { cn } from '../lib/utils';

export interface VideoPreviewProps {
  /** Video stream to display */
  stream: MediaStream | null;
  /** Whether the video is loading */
  isLoading?: boolean;
  /** Whether the camera is enabled */
  isCameraEnabled?: boolean;
  /** Custom class name */
  className?: string;
}

export const VideoPreview = ({
  stream,
  isLoading = false,
  isCameraEnabled = true,
  className,
}: VideoPreviewProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream, isCameraEnabled]);

  const showVideo = stream && isCameraEnabled && !isLoading;
  const showPlaceholder = !showVideo;

  return (
    <div
      className={cn(
        'relative flex items-center justify-center overflow-hidden rounded-2xl bg-slate-200',
        'h-[380px] w-[560px]',
        className
      )}
    >
      {showVideo && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="h-full w-full object-cover"
        />
      )}

      {showPlaceholder && (
        <div className="flex flex-col items-center justify-center gap-3 text-slate-400">
          {isLoading ? (
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500" />
          ) : !isCameraEnabled ? (
            <>
              <VideoOff className="h-12 w-12" />
              <span className="text-sm">Camera is off</span>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};
